import { QestlabListDropdown } from 'Components/List';
import ListType from 'Components/List/QestlabListDropdown/ListType';
import { WorksheetEquipmentDropdown } from 'Components/Worksheets/Core';
import { FdMeasurementApplication } from 'Components/Worksheets/NuclearFieldDensityWorksheet/types';
import { hasValue } from 'Lib/Any';
import { Guid } from 'Lib/Guid';
import { combine, pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import { useSelector } from 'react-redux';
import { Field, NumericInput, RadioGroup, TextOutput } from '../../../../Core';
import { withInjectedProps } from '../../../../Core/InjectedPropsContext';
import { FieldSet, ListFieldSection } from '../../../../Layout';
import { WorksheetBalanceFieldSet } from '../../../Core/WorksheetEquipment';
import { IWorksheetComponent } from '../../../Core/types';
import { SandBulkDensityCheck } from '../../types';

let PreparationSectionBase = ({ createWorksheetSelector }: IWorksheetComponent): JSX.Element => {
  const sandPouringId = useSelector(createWorksheetSelector!('Preparation.SandPouringApparatus.Id')) as Guid;
  const sandBatchId = useSelector(createWorksheetSelector!('Preparation.SandBatch.Id')) as Guid;
  const sandConeCalibrationImportMismatch = useSelector(
    createWorksheetSelector!('Preparation.SandConePouringApparatusCalibrationImportMismatch')
  );
  const sandBatchCalibrationImportMismatch = useSelector(
    createWorksheetSelector!('Preparation.SandBatchCalibrationImportMismatch')
  );
  const sandInternalDiameter = useSelector(createWorksheetSelector!('Preparation.InternalDiameter'), deepEquals);
  const bulkDensityofSand = useSelector(createWorksheetSelector!('Preparation.BulkDensityOfSand'), deepEquals);
  const calibrationInitialMassOfSand = useSelector(
    createWorksheetSelector!('Preparation.CalibrationInitialMassOfSand'),
    deepEquals
  );
  const calibrationMassOfSandInCone = useSelector(
    createWorksheetSelector!('Preparation.CalibrationMassOfSandInCone'),
    deepEquals
  );
  const shouldShowMassofSandinCone = useSelector(
    pipe(createWorksheetSelector!('Preparation.SandBulkDensityCheck'), (d) => d === SandBulkDensityCheck.Check)
  );

  const confirmMeasurementApplicationChange = useSelector(
    combine(
      [
        'Preparation.MeasurementApplication',
        'Retest.IsRetest',
        'IsParticleDensityAssumed',
        'Retest.OriginalTest.ImportedTest',
        'ParticleDensityAssumed',
        'ReferenceDensity.ImportedTest',
      ].map((f) => createWorksheetSelector!(f)),
      (measurementApplication, isRetest, isParticleDensityAssumed, ...fieldsClearedForNonComplianceTests) =>
        measurementApplication === FdMeasurementApplication.ComplianceTest &&
        (isRetest ||
          isParticleDensityAssumed === false || // The default state of this field is always true. This won't detect the field being changed and changed back but we're more concerned about losing ParticleDensityAssumed anyway.
          fieldsClearedForNonComplianceTests.some(hasValue))
    )
  );
  const testNumber = useSelector(createWorksheetSelector!('Preparation.TestNumber'));
  return (
    <ListFieldSection>
      <Field input={<TextOutput />} name="Preparation.TestNumber" visible={hasValue(testNumber)} />
      <Field
        input={<QestlabListDropdown />}
        label="Weather"
        name="Preparation.Weather"
        listType={ListType.Weather}
        maxLength={50}
      />
      <Field
        input={<RadioGroup />}
        name="Preparation.MeasurementApplication" //See index for list of options
        confirm={confirmMeasurementApplicationChange}
        confirmText={`This action will delete data entered in the Retest and Laboratory Maximum Density sections. Are you sure you want to continue?`}
      />
      <FieldSet>
        <Field
          input={<WorksheetEquipmentDropdown />}
          label="Sand Pouring Apparatus"
          equipmentType="sand-cone-pouring-apparatus"
          name="Preparation.SandPouringApparatus"
          importedCalibration={{
            Mismatch: sandConeCalibrationImportMismatch,
            InternalDiameter: sandInternalDiameter,
            MassOfSand: calibrationInitialMassOfSand,
            MassOfSandInCone: calibrationMassOfSandInCone,
          }}
          importModel={{ Id: sandPouringId }}
        />
        <Field input={<TextOutput />} label="Internal Diameter" name="Preparation.InternalDiameter" />
        <Field input={<TextOutput />} label="Initial Mass of Sand" name="Preparation.CalibrationInitialMassOfSand" />
        <Field input={<TextOutput />} label="Mass of Sand in Cone" name="Preparation.CalibrationMassOfSandInCone" />
      </FieldSet>
      <FieldSet>
        <Field
          input={<WorksheetEquipmentDropdown />}
          label="Sand Batch"
          equipmentType="sand-bulk-density"
          name="Preparation.SandBatch"
          importedCalibration={{
            Mismatch: sandBatchCalibrationImportMismatch,
            BulkDensity: bulkDensityofSand,
          }}
          importModel={{ Id: sandBatchId }}
        />
        <Field input={<TextOutput />} label="Bulk Density of Sand" name="Preparation.BulkDensityOfSand" />
      </FieldSet>
      <WorksheetBalanceFieldSet label="Balance" name="Preparation.Balance" />
      <Field
        input={<RadioGroup />}
        label="Sand Bulk Density Check"
        name="Preparation.SandBulkDensityCheck"
        options={[
          { label: 'None (already carried out)', value: SandBulkDensityCheck.None },
          { label: 'Check (sand in cone)', value: SandBulkDensityCheck.Check },
        ]}
      />
      <Field
        input={<NumericInput />}
        label="Mass of Sand in Cone Check Mass"
        name="Preparation.MassOfSandInCone"
        visible={shouldShowMassofSandinCone}
      />
    </ListFieldSection>
  );
};

const PreparationSection = withInjectedProps(PreparationSectionBase);
export { PreparationSection };
export default PreparationSection;
